import { useMemo } from 'react';

interface CrisisStatusProps {
  status?: 'not-active' | 'lih-assigned' | 'crisis-declared' | 'active-response' | 'return-bau' | 'deactivation';
  declarationType?: 'cyber-attack' | 'data-breach' | 'system-outage' | 'ransomware';
  impactLevel?: 'critical' | 'high' | 'medium' | 'low';
}

const statusConfig = {
  'not-active': { letter: 'A', color: '#4B5563' },      // Cool gray
  'lih-assigned': { letter: 'B', color: '#F59E0B' },    // Amber
  'crisis-declared': { letter: 'C', color: '#EF4444' }, // Red
  'active-response': { letter: 'D', color: '#FBBF24' }, // Yellow
  'return-bau': { letter: 'E', color: '#3B82F6' },      // Blue
  'deactivation': { letter: 'F', color: '#10B981' },    // Emerald
};

export default function CrisisStatus({ 
  status = 'lih-assigned',
  declarationType,
  impactLevel
}: CrisisStatusProps) {
  const statusColor = useMemo(() => {
    switch (status) {
      case 'not-active':
        return 'text-gray-600';
      case 'lih-assigned':
        return 'text-amber-500';
      case 'crisis-declared':
        return 'text-red-500';
      case 'active-response':
        return 'text-yellow-500';
      case 'return-bau':
        return 'text-blue-500';
      case 'deactivation':
        return 'text-emerald-500';
      default:
        return 'text-gray-600';
    }
  }, [status]);

  const statusText = useMemo(() => {
    switch (status) {
      case 'not-active':
        return 'A - Not Active';
      case 'lih-assigned':
        return 'B - LIH Assigned';
      case 'crisis-declared':
        return 'C - Crisis Declared';
      case 'active-response':
        return 'D - Active Crisis Response';
      case 'return-bau':
        return 'E - Return to BAU';
      case 'deactivation':
        return 'F - Deactivation';
      default:
        return 'A - Not Active';
    }
  }, [status]);

  const impactColor = useMemo(() => {
    switch (impactLevel) {
      case 'critical':
        return 'text-red-600';
      case 'high':
        return 'text-orange-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-400';
    }
  }, [impactLevel]);

  return (
    <div className="bg-white rounded-lg shadow h-[250px]">
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Current Status</p>
                <p className={`text-lg font-medium ${statusColor}`}>
                  {statusText}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Declaration Type</p>
                <p className="text-lg font-medium text-gray-400">
                  {declarationType ? declarationType.split('-').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ') : 'Not Declared'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Impact Level</p>
                <p className={`text-lg font-medium ${impactColor}`}>
                  {impactLevel ? impactLevel.charAt(0).toUpperCase() + impactLevel.slice(1) : 'Not Set'}
                </p>
              </div>
            </div>
          </div>
          <div className="w-32 h-32 relative">
            <svg className="w-full h-full" viewBox="0 0 36 36">
              {Object.entries(statusConfig).map(([statusKey, config], index, array) => {
                const angleSize = 360 / array.length;
                const startAngle = index * angleSize - 90; // Start from top
                const endAngle = (index + 1) * angleSize - 90;
                const startRad = (startAngle * Math.PI) / 180;
                const endRad = (endAngle * Math.PI) / 180;
                const radius = 16;

                const x1 = 18 + radius * Math.cos(startRad);
                const y1 = 18 + radius * Math.sin(startRad);
                const x2 = 18 + radius * Math.cos(endRad);
                const y2 = 18 + radius * Math.sin(endRad);

                // Calculate position for the letter
                const midAngle = (startAngle + endAngle) / 2;
                const midRad = (midAngle * Math.PI) / 180;
                const letterRadius = 12;
                const letterX = 18 + letterRadius * Math.cos(midRad);
                const letterY = 18 + letterRadius * Math.sin(midRad);

                const largeArcFlag = angleSize <= 180 ? 0 : 1;

                const d = [
                  `M 18 18`,
                  `L ${x1} ${y1}`,
                  `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                  'Z'
                ].join(' ');

                return (
                  <g key={statusKey}>
                    <path
                      d={d}
                      fill={config.color}
                      stroke="white"
                      strokeWidth="0.5"
                      opacity={status === statusKey ? 1 : 0.3}
                    />
                    <text
                      x={letterX}
                      y={letterY}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="white"
                      fontSize="2.5"
                      fontWeight="bold"
                      opacity={status === statusKey ? 1 : 0.5}
                    >
                      {config.letter}
                    </text>
                  </g>
                );
              })}
              <circle cx="18" cy="18" r="8" fill="white" stroke="#E5E7EB" strokeWidth="0.5"/>
              <text
                x="18"
                y="18"
                textAnchor="middle"
                dominantBaseline="middle"
                fill={statusConfig[status].color}
                fontSize="6"
                fontWeight="bold"
              >
                {statusConfig[status].letter}
              </text>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}